import { Injectable } from '@angular/core';
import Auth, { CognitoUser } from '@aws-amplify/auth';
import { environment } from '@env/environment';
/* Rxjs */
import { BehaviorSubject, from, Observable, of, switchMap, tap } from 'rxjs';

/* Models */
import { AuthEnum } from '../models/auth.model';
/* Services */
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  redirectUrl = '/';
  authState: AuthEnum = AuthEnum.SIGN_IN;
  isSignedIn$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(private userService: UserService) {
    this.init();
  }

  private init(): void {
    Auth.configure({
      storage: window.sessionStorage,
      region: environment.COGNITO_REGION,
      userPoolId: environment.COGNITO_POOL_ID,
      userPoolWebClientId: environment.COGNITO_CLIENT_ID
    });
  }

  signIn(username: string, password: string): Observable<CognitoUser | any> {
    return from(Auth.signIn({ username: username, password: password }));
  }

  signOut(): Observable<any> {
    localStorage.clear();
    return from(Auth.signOut()).pipe(
      tap(() => {
        this.userService.setUser(null);
        this.setAuthState(AuthEnum.SIGN_IN);
      })
    );
  }

  getAccessToken(): Observable<string> {
    // NOTE AdamP - we're sending the id token for now to get the email field in the jwt
    return from(Auth.currentSession()).pipe(switchMap(session => of(session.getIdToken().getJwtToken())));
  }

  getAuthState(): AuthEnum | null {
    return this.authState;
  }

  setAuthState(authState: AuthEnum): void {
    this.authState = authState;
  }

  async isSignedIn(): Promise<boolean> {
    try {
      // Get user
      const user: CognitoUser | any = await Auth.currentAuthenticatedUser();
      // Check expiry
      const expiry = user?.getSignInUserSession()?.getIdToken()?.getExpiration() || 0;
      const now = Math.floor(Date.now() / 1000); // current time in seconds
      const notExpired = expiry > now;

      this.userService.setUser(notExpired ? user : null);
      this.isSignedIn$.next(notExpired);

      return notExpired;
    } catch (error) {
      this.isSignedIn$.next(false);
      return false;
    }
  }

  async completeNewPassword(user: CognitoUser | any, newPassword: string): Promise<CognitoUser | any> {
    return await Auth.completeNewPassword(user, newPassword);
  }

  async forgotPassword(username: string): Promise<any> {
    return await Auth.forgotPassword(username);
  }

  async forgotPasswordSubmit(username: string, code: string, new_password: string): Promise<any> {
    return await Auth.forgotPasswordSubmit(username, code, new_password);
  }
}
